import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  increment,
  limit,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { SITE_URL } from '../../config';
import { db } from '../../firebase';
import RichContent from '../common/RichContent';
import { stripMarkdown } from '../../utils/stripMarkdown';
import BackButton from '../common/BackButton';
import ShareBar from '../common/ShareBar';
import { Spinner } from '../common/LoadingIndicators';

function safeText(value) {
  return String(value || '').trim();
}

function buildMetaDescription(body) {
  const plain = stripMarkdown(body);
  if (!plain) return 'iGaming slot review.';
  return plain.length > 180 ? `${plain.slice(0, 180).trim()}…` : plain;
}

function SlotReviewPage() {
  const { slugOrId } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasRecordedView = useRef(false);

  useEffect(() => {
    let unsub = null;
    let cancelled = false;
    hasRecordedView.current = false;

    async function load() {
      setLoading(true);
      setReview(null);

      // Prefer slug-based lookup for SEO-friendly URLs
      try {
        const q = query(collection(db, 'slotReviews'), where('slug', '==', slugOrId), limit(1));
        unsub = onSnapshot(
          q,
          async (snap) => {
            if (cancelled) return;
            if (!snap.empty) {
              const docSnap = snap.docs[0];
              setReview({ id: docSnap.id, ...docSnap.data() });
              if (!hasRecordedView.current) {
                hasRecordedView.current = true;
                try {
                  updateDoc(doc(db, 'slotReviews', docSnap.id), { views: increment(1) });
                } catch {
                  // ignore view update errors
                }
              }
              setLoading(false);
              return;
            }

            // Fallback: treat param as document id
            try {
              const byId = await getDoc(doc(db, 'slotReviews', slugOrId));
              if (byId.exists()) {
                setReview({ id: byId.id, ...byId.data() });
                if (!hasRecordedView.current) {
                  hasRecordedView.current = true;
                  try {
                    updateDoc(doc(db, 'slotReviews', byId.id), { views: increment(1) });
                  } catch {
                    // ignore view update errors
                  }
                }
              } else {
                setReview(null);
              }
            } catch {
              setReview(null);
            }
            setLoading(false);
          },
          () => {
            // If slug query fails, fallback to id lookup once
          }
        );
      } catch {
        try {
          const byId = await getDoc(doc(db, 'slotReviews', slugOrId));
          if (!cancelled && byId.exists()) {
            setReview({ id: byId.id, ...byId.data() });
          } else if (!cancelled) {
            setReview(null);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [slugOrId]);

  const canonical = `${SITE_URL}/slots/${(review && (review.slug || review.id)) || slugOrId}`;
  const title = useMemo(() => safeText(review?.title) || 'Slot review', [review]);
  const provider = useMemo(() => safeText(review?.provider), [review]);
  const imageUrl = useMemo(() => safeText(review?.imageUrl), [review]);
  const description = useMemo(() => buildMetaDescription(review?.body), [review]);
  const toIso = (value) => {
    if (!value) return undefined;
    if (typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    }
    if (value?.toDate) {
      try {
        return value.toDate().toISOString();
      } catch {
        return undefined;
      }
    }
    try {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    } catch {
      return undefined;
    }
  };

  if (loading) {
    return (
      <section className="content-card">
        <p className="secondary-text" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner /> Loading slot review…
        </p>
      </section>
    );
  }

  if (!review) {
    return (
      <section className="content-card">
        <Helmet>
          <title>Slot review not found – IGAMINGSPOT</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <h1 className="section-subtitle">Slot review not found</h1>
        <p className="secondary-text">This review is not available or has been removed.</p>
        <div style={{ marginTop: 12 }}>
          <Link to="/slots" className="latest-see-all">
            Back to slot reviews →
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article className="content-card slot-review-page" aria-label="Slot review">
      <Helmet>
        <title>{`${title}${provider ? ` – ${provider}` : ''} – Slot review`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${title}${provider ? ` – ${provider}` : ''}`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        <meta property="og:site_name" content="IGAMINGSPOT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title}${provider ? ` – ${provider}` : ''}`} />
        <meta name="twitter:description" content={description} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${title}${provider ? ` – ${provider}` : ''}`,
            description,
            image: imageUrl ? [imageUrl] : [],
            datePublished: toIso(review?.createdAt || review?.updatedAt),
            dateModified: toIso(review?.updatedAt || review?.createdAt),
            author: {
              '@type': 'Organization',
              name: 'IGAMINGSPOT',
            },
            publisher: {
              '@type': 'Organization',
              name: 'IGAMINGSPOT',
              logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/logo512.png`,
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonical,
            },
          })}
        </script>
      </Helmet>

      <header className="content-card-header">
        <div>
          <div className="section-title">Slot review</div>
          <h1 className="section-subtitle">{title}</h1>
        </div>
        <BackButton fallbackTo="/slots" label="Back" />
      </header>

      {provider && (
        <p className="secondary-text">
          Provider: <strong>{provider}</strong>
        </p>
      )}

      {imageUrl && (
        <div className="slot-review-hero">
          <img src={imageUrl} alt={title} />
        </div>
      )}

      <div className="article-body">
        <RichContent content={review.body} forceAutoFormat />
      </div>

      <ShareBar
        url={canonical}
        title={`${title}${provider ? ` – ${provider}` : ''}`}
        text={description}
        label="Share"
        compact
      />

      <div style={{ marginTop: 16 }}>
        <Link to="/slots" className="latest-see-all">
          More slot reviews →
        </Link>
      </div>
    </article>
  );
}

export default SlotReviewPage;


